import { applyInParagraphSuggestion } from './applyInParagraphSuggestion';
import { applyInsertRowsSuggestion } from './applyInsertRowsSuggestion';
import { applyInsertSuggestion } from './applyInsertSuggestion';
import { applyRemoveTableSuggestion } from './applyRemoveTableSuggestion';
import {
  applyRemoveColumnsSuggestion,
  applyRemoveRowsSuggestion,
  applyRemoveSuggestion,
} from './removePipeline';
import type {
  HtmlEditApplierDeps,
  HtmlEditApplierMode,
  PreviewHtmlEditDeps,
  SuggestionEditorPool,
} from './shared';
import { generateSuggestedEditGroupId } from '../../slate-api';
import type { HtmlEdit } from '../redline-llm-output';
import type { CodeBlockRedlineItem, CodeBlockRedlineNote } from '../redline-shapes';

/** Cap for the removed-content plain-text preview shown on the chat card. */
const REMOVE_PREVIEW_MAX_CHARS = 500;

function truncatePreview(text: string): string {
  if (text.length <= REMOVE_PREVIEW_MAX_CHARS) return text;
  return `${text.slice(0, REMOVE_PREVIEW_MAX_CHARS)}…`;
}

/**
 * Materialize one `HtmlEdit` into the host's editor as source-stamped
 * suggested edits AND convert it into the `CodeBlockRedlineItem`s the client
 * will render as cards — usually one item, but a `remove` expands into one
 * `redline-remove` per contiguous run of removed paragraphs, and every
 * failure mode becomes a `redline-edit-error`.
 *
 * This function is intentionally non-pure: success cases mutate the editor
 * before returning.
 *
 * PDF (Apryse) documents are read-only — their coordinate-based paragraph
 * anchors don't exist in the collab editor, so applying would throw. Each
 * edit that targets a PDF anchor downgrades to the preview path (the same
 * one `previewHtmlEdit` runs): the card is built and returned, nothing is
 * applied. Signing-locked documents (sent for signature or signed, per the
 * editor pool's `getSigningLockState`) get the same chat-only treatment.
 *
 * Responsibilities:
 *  - Strip stray selection-range markers the LLM may have echoed back.
 *  - Parse the proposed HTML into ranges keyed against the existing paragraph.
 *  - Apply the edit to the host's editor (skipped for read-only PDF
 *    documents).
 *  - Convert each failure mode (missing `<p>`, missing paragraph, throw from
 *    apply) into a typed `redline-edit-error` so the stream stays well-formed.
 */
export async function materializeHtmlEdit(
  object: HtmlEdit,
  deps: HtmlEditApplierDeps
): Promise<CodeBlockRedlineItem[]> {
  return applyHtmlEdit(object, deps, 'materialize');
}

/**
 * Preview mode must never open an editor. The appliers' mode guards make
 * this unreachable; if a guard regresses, the thrown error surfaces as that
 * edit's `redline-edit-error` card instead of a silent document write.
 */
const previewModeEditorPool: SuggestionEditorPool = {
  getOrCreateEditor: () => {
    throw new Error('previewHtmlEdit must not open an editor.');
  },
  /**
   * Signing locks only gate document writes, and preview never writes — the
   * mode guards short-circuit before consulting the lock. Answer "unlocked"
   * so a stray read stays harmless; a regressed guard still trips the
   * `getOrCreateEditor` throw above.
   */
  getSigningLockState: () => Promise.resolve(null),
};

/**
 * Build the cards for one `HtmlEdit` WITHOUT touching any document — the
 * validation, card shapes, and error modes are identical to
 * `materializeHtmlEdit`, but nothing is applied and no editor is opened.
 * This is the path a deferred-materialization pipeline (playbooks) previews
 * with; in chat it is what every PDF-anchored edit already runs.
 */
export async function previewHtmlEdit(
  object: HtmlEdit,
  deps: PreviewHtmlEditDeps
): Promise<CodeBlockRedlineItem[]> {
  return applyHtmlEdit(object, { ...deps, editorPool: previewModeEditorPool }, 'preview');
}

async function applyHtmlEdit(
  object: HtmlEdit,
  deps: HtmlEditApplierDeps,
  mode: HtmlEditApplierMode
): Promise<CodeBlockRedlineItem[]> {
  const { suggest } = object;

  switch (suggest._type) {
    case 'insert':
      return [await applyInsertSuggestion(suggest, deps, mode)];
    case 'remove':
      return applyRemoveSuggestion(suggest, deps, mode);
    case 'remove-rows':
      return applyRemoveRowsSuggestion(suggest, deps, mode);
    case 'remove-columns':
      return applyRemoveColumnsSuggestion(suggest, deps, mode);
    case 'remove-table':
      return [await applyRemoveTableSuggestion(suggest, deps, mode)];
    /**
     * Notes are the model's only user-facing text channel (its plain-text
     * output is discarded by the stream handler): a short explanation of why
     * an edit was NOT made, rendered as a neutral card. Nothing touches the
     * editor in either mode.
     */
    case 'note':
      return [
        {
          documentId: suggest.documentId,
          groupId: generateSuggestedEditGroupId(),
          text: truncatePreview(suggest.text),
          type: 'redline-note',
        } satisfies CodeBlockRedlineNote,
      ];
    case 'insert-rows':
      return [await applyInsertRowsSuggestion(suggest, deps, mode)];
    case 'edit-paragraph':
      return applyInParagraphSuggestion(suggest, deps, mode);
  }
}
